import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Shield, X, AlertTriangle, RefreshCw, CheckCircle, Clock } from 'lucide-react';
import { UserProfile } from '../types';
import { auth } from '../lib/firebase';
import DiditVerificationGate from './DiditVerificationGate';

interface VerificationBannerProps {
  userProfile: UserProfile;
  onStatusChange: () => void;
}

export default function VerificationBanner({ userProfile, onStatusChange }: VerificationBannerProps) {
  const [showModal, setShowModal] = useState(false);
  // §3.8: dismissal persists SERVER-SIDE per account, keyed to the
  // verification status it was dismissed for (users/{uid}
  // .diditBannerDismissedStatus, written via PUT /api/profile/update).
  // The old sessionStorage flag died with the browser session, so the
  // banner came back on every login. Local state only covers the gap
  // until the refreshed profile lands.
  const [dismissedLocally, setDismissedLocally] = useState(false);

  const didit = userProfile.didit;
  const status = didit?.status;
  const serverDismissed = !!status && userProfile.diditBannerDismissedStatus === status;
  const dismissed = dismissedLocally || serverDismissed;

  const handleDismiss = () => {
    setDismissedLocally(true);
    // Persist for this exact status — a later status change re-shows the
    // banner because the stored value no longer matches.
    void (async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        if (!token || !status) return;
        await fetch('/api/profile/update', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ diditBannerDismissedStatus: status }),
        });
      } catch {
        /* the local dismissal still applies for this session */
      }
    })();
  };
  const [lastStatus, setLastStatus] = useState<string | undefined>(userProfile.didit?.status);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Track status changes locally so we can trigger onStatusChange when
  // a manual/backend approval lands while the banner is visible.
  useEffect(() => {
    if (status === 'approved' && lastStatus !== 'approved') {
      // Just became approved — refresh profile so the banner hides
      onStatusChange();
    }
    setLastStatus(status);
  }, [status, lastStatus, onStatusChange]);

  // Poll Didit while the banner is visible, pausing when the tab is hidden.
  useEffect(() => {
    let tabVisible = true;
    const handleVisibility = () => { tabVisible = !document.hidden; };
    document.addEventListener('visibilitychange', handleVisibility);

    const poll = async () => {
      if (!tabVisible) return;
      const token = await auth.currentUser?.getIdToken();
      if (!token) return;
      try {
        const res = await fetch('/api/auth/didit/retrieve-session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.status === 'approved') {
          setShowModal(false);
          onStatusChange();
        }
      } catch {
        // Silently ignore network hiccups
      }
    };

    poll();
    pollRef.current = setInterval(poll, 60_000);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [onStatusChange]);

  // Don't show if verified, not started (gate triggers at Submit instead),
  // or if banner was dismissed
  if (status === 'approved' || status === 'not_started' || dismissed) return null;

  const isBlocker = status === 'reverification_required';

  // Active verification states — session exists and is still live
  const isInProgress = status === 'pending' || status === 'in_progress' || status === 'in_review';

  // States where the session is still active and can be resumed (has a valid sessionUrl)
  const canResume = status === 'pending' || status === 'in_progress';

  const badgeConfig: Record<string, { icon: React.ElementType; label: string; color: string }> = {
    pending: { icon: Clock, label: 'Verification Pending', color: 'bg-amber-50 border-amber-200 text-amber-700' },
    in_progress: { icon: Clock, label: 'Verification In Progress', color: 'bg-amber-50 border-amber-200 text-amber-700' },
    in_review: { icon: Clock, label: 'Pending Manual Review', color: 'bg-amber-50 border-amber-200 text-amber-700' },
    declined: { icon: AlertTriangle, label: 'Verification Failed', color: 'bg-red-50 border-red-200 text-red-700' },
    expired: { icon: AlertTriangle, label: 'Verification Expired', color: 'bg-red-50 border-red-200 text-red-700' },
    abandoned: { icon: AlertTriangle, label: 'Verification Not Completed', color: 'bg-amber-50 border-amber-200 text-amber-700' },
    kyc_expired: { icon: AlertTriangle, label: 'Verification Requires Renewal', color: 'bg-amber-50 border-amber-200 text-amber-700' },
    reverification_required: { icon: RefreshCw, label: 'Re-Verification Required', color: 'bg-red-50 border-red-200 text-red-700' },
  };
  const badge = badgeConfig[status as keyof typeof badgeConfig] || badgeConfig.pending;

  return (
    <>
      <div
        className={`${isBlocker ? 'bg-red-50 border-red-200' : isInProgress ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'} border-b px-4 py-3`}
        role="alert"
        aria-live="polite"
      >
        <div className="max-w-7xl mx-auto flex items-start gap-3">
          <div className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${isBlocker ? 'bg-red-100' : isInProgress ? 'bg-blue-100' : 'bg-amber-100'}`}>
            {React.createElement(badge.icon, { className: `h-4 w-4 ${isBlocker ? 'text-red-600' : isInProgress ? 'text-brand' : 'text-amber-600'}` })}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <p className={`text-xs font-bold ${isBlocker ? 'text-red-800' : isInProgress ? 'text-blue-800' : 'text-amber-800'}`}>
                {isBlocker ? 'Re-Verification Required' : isInProgress ? 'Verification In Progress' : 'Identity Verification Required'}
              </p>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${badge.color}`}>
                {badge.label}
              </span>
            </div>
            <p className={`text-xs ${isBlocker ? 'text-red-700' : isInProgress ? 'text-brand' : 'text-amber-700'}`}>
              {isBlocker
                ? 'An administrator has requested a new identity verification. You must complete it before submitting any application.'
                : canResume
                  ? 'You have an existing verification session. Click Resume to continue where you left off.'
                  : status === 'in_review'
                    ? 'Your identity verification is being processed. You will be notified once the review is complete.'
                    : 'You can continue exploring the platform, but you must complete identity verification before submitting any application.'}
            </p>
            <button
              onClick={() => setShowModal(true)}
              className={`mt-2 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                isBlocker
                  ? 'bg-red-600 hover:bg-red-700 text-white shadow-sm'
                  : 'btn-primary  text-white shadow-sm'
              }`}
            >
              <Shield className="h-3.5 w-3.5" />
              {status === 'reverification_required'
                ? 'Complete Re-Verification'
                : canResume
                  ? 'Resume Verification'
                  : 'Verify Now'}
            </button>
          </div>
          {!isBlocker && status !== 'in_review' && (
            <button
              onClick={handleDismiss}
              className="shrink-0 p-1 rounded-lg text-amber-500 hover:text-amber-700 hover:bg-amber-100 transition-all cursor-pointer"
              aria-label="Dismiss notification"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {showModal && (
        <DiditVerificationGate
          userProfile={userProfile}
          asModal
          onComplete={() => {
            setShowModal(false);
            onStatusChange();
          }}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}
